import { Button } from '@apollo/orbit';
import classnames from 'classnames';
import { parse } from 'graphql';
import React, { useCallback, useEffect, useState } from 'react';

import { useEditorModel } from 'src/app/graph/explorerPage/hooks/useEditorModel';
import { useForceLightMonacoTheme } from 'src/app/graph/shared/monacoHelpers/monacoHelpers';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import {
  StudioEditorContainer,
  useStudioEditor,
} from 'src/hooks/useStudioEditor';

import { newSupergraphOnboardingErrorMessages } from '../shared';

export const UploadSchema = ({
  setSchemaUploaded,
  sdl,
  setSdl,
}: {
  setSchemaUploaded: (value: boolean) => void;
  sdl: string;
  setSdl: (value: string) => void;
}) => {
  const [isDraggedOver, setIsDraggedOver] = useState(false);
  const [error, setError] = useState('');
  const [manualSDL, setManualSDL] = useState(sdl);
  const [draggedSDL, setDraggedSDL] = useState<string | undefined>(undefined);

  useForceLightMonacoTheme();
  const graphqlEditor = useStudioEditor({
    language: 'graphql',
    defaultValue:
      sdl ||
      `# Paste your schema or drag a file (.gql, .graphql)











`,
  });
  const graphqlEditorModel = useEditorModel(graphqlEditor);

  // update the manualSDL while typing in the editor
  useEffect(() => {
    graphqlEditorModel?.onDidChangeContent(() => {
      setManualSDL(graphqlEditorModel.getValue());
    });
    // dispose model on cleanup
    return () => graphqlEditorModel?.dispose();
  }, [graphqlEditorModel]);

  // when someone drags & drops, set the editor content
  useEffect(() => {
    if (draggedSDL) {
      graphqlEditorModel?.setValue(draggedSDL);
    }
  }, [draggedSDL, graphqlEditorModel]);

  const uploadSchema = useCallback(() => {
    const trimmedSDL = manualSDL.trim();
    try {
      if (trimmedSDL.length > 0) {
        parse(manualSDL);
        setSdl(manualSDL);
        setSchemaUploaded(true);
      } else {
        setSdl('');
        setSchemaUploaded(false);
      }
    } catch {
      setError(newSupergraphOnboardingErrorMessages.sdl.invalid);
    }
  }, [manualSDL, setSdl, setSchemaUploaded]);

  return (
    <>
      {error && <ErrorMessage className="mb-4">{error}</ErrorMessage>}
      <div
        data-testid="sdl_editor"
        className="relative"
        onDragOver={(e) => {
          e.preventDefault();
          setIsDraggedOver(true);
        }}
        onDragEnter={(e) => {
          e.preventDefault();
          setIsDraggedOver(true);
        }}
        onDragEnd={(e) => {
          e.preventDefault();
          setIsDraggedOver(false);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          setIsDraggedOver(false);
        }}
        onDrop={(event) => {
          event.preventDefault();
          setIsDraggedOver(false);

          if (event.dataTransfer.files) {
            const file = event.dataTransfer.files[0];

            if (file && (file.type.indexOf('text') > -1 || !file.type)) {
              const reader = new FileReader();
              reader.addEventListener('load', function handleFileLoad(e) {
                const data = e.target?.result;
                if (typeof data === 'string') setDraggedSDL(data);
                if (error) setError('');
                reader.removeEventListener('load', handleFileLoad);
              });
              reader.readAsText(file);
            } else {
              setError(
                'Cannot read this type of file. Please drop a .graphql schema file.',
              );
            }
          }
        }}
      >
        <StudioEditorContainer
          editor={graphqlEditor}
          className={classnames(
            'relative h-72 rounded border p-0 py-2 text-sm',
            isDraggedOver
              ? 'border-blue-lighter bg-silver'
              : 'border-silver-darker bg-silver-lighter',
          )}
        />
      </div>
      <Button
        data-testid="supergraph-wizard-upload-sdl-button"
        className="mt-2"
        onClick={uploadSchema}
        type="button"
        variant="primary"
      >
        Upload Schema
      </Button>
    </>
  );
};
